import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export const LeadForm = ({ defaultUnit = "general", service = "", dark = false, accent = "#4361ee", showUnitSelect = false }) => {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    business_unit: defaultUnit,
    service,
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/leads`, { ...form, lang });
      setSent(true);
      toast.success(t.contact.success);
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls = `w-full rounded-xl border px-4 py-3 text-sm font-jakarta outline-none transition-all duration-300 focus:ring-2 ${
    dark
      ? "bg-white/5 border-white/15 text-white placeholder:text-slate-500 focus:ring-[#f59c1a]/50 focus:border-[#f59c1a]/60"
      : "bg-white border-black/10 text-[#353535] placeholder:text-slate-400 focus:border-[#4361ee]/50"
  }`;

  if (sent) {
    return (
      <div data-testid="lead-form-success" className={`rounded-2xl border p-10 text-center ${dark ? "border-white/15 bg-white/5" : "border-black/10 bg-white"}`}>
        <div className="font-playfair text-3xl mb-3" style={{ color: accent }}>✓</div>
        <p className={`font-jakarta text-lg ${dark ? "text-white" : "text-[#353535]"}`}>{t.contact.success}</p>
      </div>
    );
  }

  return (
    <form data-testid="lead-form" onSubmit={submit} className="grid gap-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <input data-testid="lead-form-name-input" required value={form.name} onChange={set("name")} placeholder={t.contact.name} className={inputCls} />
        <input data-testid="lead-form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder={t.contact.email} className={inputCls} />
      </div>
      <input data-testid="lead-form-company-input" value={form.company} onChange={set("company")} placeholder={t.contact.company} className={inputCls} />
      {showUnitSelect && (
        <select
          data-testid="lead-form-business-unit-select"
          value={form.business_unit}
          onChange={set("business_unit")}
          className={`${inputCls} appearance-none cursor-pointer`}
        >
          {["general", "people", "aws", "martech"].map((u) => (
            <option key={u} value={u} className="text-black">{t.contact.units[u]}</option>
          ))}
        </select>
      )}
      <textarea data-testid="lead-form-message-input" rows={4} value={form.message} onChange={set("message")} placeholder={t.contact.message} className={`${inputCls} resize-none`} />
      <button
        data-testid="lead-form-submit-button"
        type="submit"
        disabled={sending}
        className="group inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm font-jakarta font-semibold text-white transition-all duration-300 hover:gap-4 disabled:opacity-50"
        style={{ backgroundColor: accent }}
      >
        {sending ? t.contact.sending : t.contact.submit}
        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
      </button>
    </form>
  );
};
